import {
    type KeyboardEvent,
    type RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { flushSync } from 'react-dom';
import type { GridApi } from '@ag-grid-community/core';
import type { ICSAgGridProps } from '../../ICSAgGrid';
import { columnTypes as defaultColumnTypes } from '../../config';
import type {
    AppliedFilter,
    ColumnTypeMap,
    SearchableColumn,
    SearchOption,
} from './types';
import {
    buildFilterModelFromAppliedFilters,
    createAppliedFiltersFromSearchParams,
    extractLeafColumns,
    groupByCategory,
    nextId,
    getFieldValue,
} from './utils';

const FILTER_TYPE_TO_AG: Record<string, string[]> = {
    text: ['agTextColumnFilter'],
    number: ['agNumberColumnFilter', 'agFloatColumnFilter'],
    date: ['agDateColumnFilter'],
    set: ['agSetColumnFilter'],
};

function warnFilterTypeMismatches<TData>(
    gridApi: GridApi<TData>,
    filterModel: Record<string, unknown>,
): void {
    for (const [colId, entry] of Object.entries(filterModel)) {
        const col = gridApi.getColumn(colId);
        if (!col) continue;
        const configuredFilter = col.getColDef().filter;
        const appliedFilterType = (entry as Record<string, unknown>).filterType;
        if (!configuredFilter || !appliedFilterType) continue;
        const expected = FILTER_TYPE_TO_AG[String(appliedFilterType)];
        if (expected && !expected.includes(String(configuredFilter))) {
            console.warn(
                `[ICSGridSearch] Filter type mismatch on column "${colId}": ` +
                    `column uses "${configuredFilter}" but the filter model has filterType "${appliedFilterType}". ` +
                    `The filter will be silently ignored by AG Grid.`,
            );
        }
    }
}

export type GridSearchProps<TData> = Omit<
    Partial<ICSAgGridProps<TData>>,
    'api'
> & {
    api?: GridApi<TData>;
    searchParams?: URLSearchParams;
};

export interface UseGridSearchReturn {
    inputRef: RefObject<HTMLInputElement>;
    containerRef: RefObject<HTMLDivElement>;
    appliedFilters: AppliedFilter[];
    pendingColumn: SearchableColumn | null;
    inputValue: string;
    showColumnDropdown: boolean;
    showValueDropdown: boolean;
    showDateOptionDropdown: boolean;
    showDateDropdown: boolean;
    pendingDateOption: SearchOption | null;
    selectDateOption: (option: SearchOption) => void;
    showNumberOptionDropdown: boolean;
    pendingNumberOption: SearchOption | null;
    selectNumberOption: (option: SearchOption) => void;
    handleNumberOptionKeyDown: (
        e: KeyboardEvent<HTMLButtonElement>,
        option: SearchOption,
    ) => void;
    grouped: Record<string, SearchableColumn[]>;
    filteredValueOptions: SearchOption[];
    handleInputChange: (value: string) => void;
    handleInputFocus: () => void;
    handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
    handleColumnOptionKeyDown: (
        e: KeyboardEvent<HTMLButtonElement>,
        col: SearchableColumn,
    ) => void;
    handleValueOptionKeyDown: (
        e: KeyboardEvent<HTMLButtonElement>,
        option: SearchOption,
    ) => void;
    removeFilter: (id: string) => void;
    commitOption: (option: SearchOption) => void;
    selectColumn: (col: SearchableColumn) => void;
    handleDateOptionKeyDown: (
        e: KeyboardEvent<HTMLButtonElement>,
        option: SearchOption,
    ) => void;
}

export function useGridSearch<TData>({
    api,
    columnDefs,
    columnTypes,
    onGlobalFilterChange,
    searchParams,
}: GridSearchProps<TData>): UseGridSearchReturn {
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
    const [pendingColumn, setPendingColumn] = useState<SearchableColumn | null>(
        null,
    );
    const [inputValue, setInputValue] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [valueDropdownOpen, setValueDropdownOpen] = useState(false);
    const [dateOptionDropdownOpen, setDateOptionDropdownOpen] = useState(false);
    const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
    const [pendingDateOption, setPendingDateOption] =
        useState<SearchOption | null>(null);
    const [numberOptionDropdownOpen, setNumberOptionDropdownOpen] =
        useState(false);
    const [pendingNumberOption, setPendingNumberOption] =
        useState<SearchOption | null>(null);
    const [valueOptions, setValueOptions] = useState<SearchOption[]>([]);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const appliedFiltersRef = useRef<AppliedFilter[]>([]);
    const savedFiltersRef = useRef(new Map<GridApi<TData>, AppliedFilter[]>());
    const prevApiRef = useRef<GridApi<TData> | undefined>();
    const apiSwitchedRef = useRef(false);
    const urlSeededRef = useRef(false);

    const mergedColumnTypes = useMemo<ColumnTypeMap>(
        () => ({
            ...defaultColumnTypes,
            ...(columnTypes as ColumnTypeMap | undefined),
        }),
        [columnTypes],
    );

    const searchableColumns = useMemo<SearchableColumn[]>(
        () =>
            columnDefs ? extractLeafColumns(columnDefs, mergedColumnTypes) : [],
        [columnDefs, mergedColumnTypes],
    );

    const filteredColumns = useMemo<SearchableColumn[]>(() => {
        const appliedColIds = new Set(
            appliedFilters.flatMap((f) =>
                f.column !== null ? [f.column.colId] : [],
            ),
        );
        const q = inputValue.trim().toLowerCase();
        const available = searchableColumns.filter(
            (c) => !appliedColIds.has(c.colId),
        );
        if (!q) return available;
        return available.filter(
            (c) =>
                c.headerName.toLowerCase().includes(q) ||
                Boolean(c.groupName?.toLowerCase().includes(q)),
        );
    }, [searchableColumns, inputValue, appliedFilters]);

    const filteredValueOptions = useMemo<SearchOption[]>(() => {
        const q = inputValue.trim().toLowerCase();
        if (!q) return valueOptions;
        return valueOptions.filter(
            (o) =>
                o.label.toLowerCase().includes(q) ||
                o.value.toLowerCase().includes(q),
        );
    }, [valueOptions, inputValue]);

    // Keep ref in sync for the api-switch effect
    useEffect(() => {
        appliedFiltersRef.current = appliedFilters;
    }, [appliedFilters]);

    // Seed applied filters from the URL once, on first grid mount
    useEffect(() => {
        if (urlSeededRef.current) return;
        if (!api || !searchParams || searchableColumns.length === 0) return;
        urlSeededRef.current = true;
        const seeded = createAppliedFiltersFromSearchParams(
            searchParams,
            searchableColumns,
        );
        if (seeded.length > 0) setAppliedFilters(seeded);
    }, [api, searchParams, searchableColumns]);

    // Save/restore filters when api switches (master ↔ detail)
    useEffect(() => {
        const prevApi = prevApiRef.current;
        prevApiRef.current = api;
        if (!prevApi || prevApi === api) return;
        apiSwitchedRef.current = true;
        savedFiltersRef.current.set(prevApi, appliedFiltersRef.current);
        setAppliedFilters(api ? savedFiltersRef.current.get(api) ?? [] : []);
        setInputValue('');
        setPendingColumn(null);
        setDropdownOpen(false);
        setValueDropdownOpen(false);
        setDateOptionDropdownOpen(false);
        setDateDropdownOpen(false);
        setPendingDateOption(null);
        setNumberOptionDropdownOpen(false);
        setPendingNumberOption(null);
        setValueOptions([]);
    }, [api]);

    // Apply all active filters to the grid whenever they change
    useEffect(() => {
        // Skip on the render where api switched — the api-switch effect already
        // called setAppliedFilters() to restore saved filters; applying stale
        // appliedFilters to the new api here would dirty the wrong grid.
        if (apiSwitchedRef.current) {
            apiSwitchedRef.current = false;
            return;
        }
        const gridApi = api;
        if (!gridApi) return;

        const isClientSide =
            gridApi.getGridOption('rowModelType') === 'clientSide';
        const columnFilters = appliedFilters.filter((f) => f.column !== null);
        const globalFilters = appliedFilters.filter((f) => f.column === null);

        // quickFilterText — client-side only (searches across all visible columns)
        if (isClientSide) {
            gridApi.setGridOption(
                'quickFilterText',
                globalFilters.map((f) => f.value).join(' '),
            );
        }

        // Server-side global search callback — lets the datasource forward free-text
        if (!isClientSide && typeof onGlobalFilterChange === 'function') {
            onGlobalFilterChange(globalFilters.map((f) => f.value).join(' '));
        }

        // Column filters via setFilterModel — works for both client-side and server-side
        if (columnFilters.length > 0) {
            const filterModel =
                buildFilterModelFromAppliedFilters(columnFilters);
            warnFilterTypeMismatches(gridApi, filterModel);
            gridApi.setFilterModel(filterModel);
        } else {
            gridApi.setFilterModel({});
        }
    }, [appliedFilters, api, onGlobalFilterChange]);

    const loadDynamicOptions = useCallback(
        (col: SearchableColumn): SearchOption[] => {
            const gridApi = api;
            if (!gridApi || !col.field) return [];
            const seen = new Set<string>();
            gridApi.forEachNode((node) => {
                if (!node.data) return;
                const v = String(
                    getFieldValue(node.data, col.field ?? col.colId) ?? '',
                );
                if (v) seen.add(v);
            });
            return Array.from(seen)
                .sort()
                .map((v) => ({ value: v, label: v }));
        },
        [api],
    );

    const getOptions = useCallback(
        (): HTMLButtonElement[] =>
            Array.from(
                containerRef.current?.querySelectorAll<HTMLButtonElement>(
                    '[role="option"]',
                ) ?? [],
            ),
        [],
    );

    const cancelPendingColumn = useCallback(() => {
        flushSync(() => {
            setPendingColumn(null);
            setInputValue('');
            setValueDropdownOpen(false);
            setDateOptionDropdownOpen(false);
            setDateDropdownOpen(false);
            setPendingDateOption(null);
            setNumberOptionDropdownOpen(false);
            setPendingNumberOption(null);
            setValueOptions([]);
        });
        inputRef.current?.focus();
    }, []);

    const commitFilter = useCallback(() => {
        const value = inputValue.trim();
        if (!value) return;
        setAppliedFilters((prev) => [
            ...prev,
            {
                id: nextId(),
                column: pendingColumn,
                value,
                label: pendingNumberOption
                    ? `${pendingNumberOption.label} ${value}`
                    : undefined,
                filterOption: pendingNumberOption?.value,
            },
        ]);
        setInputValue('');
        setPendingColumn(null);
        setPendingNumberOption(null);
        setDropdownOpen(false);
    }, [inputValue, pendingColumn, pendingNumberOption]);

    const commitOption = useCallback(
        (option: SearchOption) => {
            flushSync(() => {
                setAppliedFilters((prev) => [
                    ...prev,
                    {
                        id: nextId(),
                        column: pendingColumn,
                        value: option.value,
                        label:
                            pendingColumn?.inputType === 'date' &&
                            pendingDateOption
                                ? `${pendingDateOption.label} ${option.label}`
                                : option.label,
                        filterOption: pendingDateOption?.value,
                    },
                ]);
                setInputValue('');
                setPendingColumn(null);
                setPendingDateOption(null);
                setDropdownOpen(false);
                setValueDropdownOpen(false);
                setDateOptionDropdownOpen(false);
                setDateDropdownOpen(false);
                setValueOptions([]);
            });
            inputRef.current?.focus();
        },
        [pendingColumn, pendingDateOption],
    );

    const removeFilter = useCallback((filterId: string) => {
        setAppliedFilters((prev) => prev.filter((f) => f.id !== filterId));
    }, []);

    const selectColumn = useCallback(
        (col: SearchableColumn) => {
            flushSync(() => {
                setPendingColumn(col);
                setInputValue('');
                setDropdownOpen(false);
                if (col.inputType === 'select') {
                    setValueOptions(col.options ?? loadDynamicOptions(col));
                    setValueDropdownOpen(true);
                } else if (col.inputType === 'date') {
                    setDateOptionDropdownOpen(true);
                } else if (col.inputType === 'number') {
                    setNumberOptionDropdownOpen(true);
                }
            });
            inputRef.current?.focus();
        },
        [loadDynamicOptions],
    );

    const selectDateOption = useCallback((option: SearchOption) => {
        flushSync(() => {
            setPendingDateOption(option);
            setDateOptionDropdownOpen(false);
            setDateDropdownOpen(true);
        });
        inputRef.current?.focus();
    }, []);

    const selectNumberOption = useCallback((option: SearchOption) => {
        flushSync(() => {
            setPendingNumberOption(option);
            setNumberOptionDropdownOpen(false);
        });
        inputRef.current?.focus();
    }, []);

    const handleInputChange = useCallback(
        (value: string) => {
            setInputValue(value);
            if (!pendingColumn) setDropdownOpen(value.length > 0);
        },
        [pendingColumn],
    );

    const handleInputFocus = useCallback(() => {
        if (!pendingColumn && inputValue.length > 0) setDropdownOpen(true);
        if (pendingColumn?.inputType === 'select') setValueDropdownOpen(true);
        if (pendingColumn?.inputType === 'date') {
            if (pendingDateOption) setDateDropdownOpen(true);
            else setDateOptionDropdownOpen(true);
        }
        if (pendingColumn?.inputType === 'number' && !pendingNumberOption) {
            setNumberOptionDropdownOpen(true);
        }
    }, [pendingColumn, pendingDateOption, pendingNumberOption, inputValue]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            new Map<string, () => void>([
                [
                    'Enter',
                    () => {
                        e.preventDefault();
                        commitFilter();
                    },
                ],
                [
                    'Escape',
                    () => {
                        if (valueDropdownOpen || numberOptionDropdownOpen) {
                            cancelPendingColumn();
                            return;
                        }
                        pendingColumn
                            ? cancelPendingColumn()
                            : setDropdownOpen(false);
                    },
                ],
                [
                    'Backspace',
                    () => {
                        if (inputValue) return;
                        pendingColumn
                            ? cancelPendingColumn()
                            : setAppliedFilters((prev) => prev.slice(0, -1));
                    },
                ],
                [
                    'ArrowDown',
                    () => {
                        if (
                            !dropdownOpen &&
                            !valueDropdownOpen &&
                            !dateOptionDropdownOpen &&
                            !numberOptionDropdownOpen
                        )
                            return;
                        e.preventDefault();
                        getOptions()[0]?.focus();
                    },
                ],
            ]).get(e.key)?.();
        },
        [
            commitFilter,
            cancelPendingColumn,
            dropdownOpen,
            valueDropdownOpen,
            dateOptionDropdownOpen,
            numberOptionDropdownOpen,
            getOptions,
            inputValue,
            pendingColumn,
        ],
    );

    const handleColumnOptionKeyDown = useCallback(
        (e: KeyboardEvent<HTMLButtonElement>, col: SearchableColumn) => {
            const options = getOptions();
            const idx = options.indexOf(e.currentTarget);
            new Map<string, () => void>([
                [
                    'Enter',
                    () => {
                        e.preventDefault();
                        selectColumn(col);
                    },
                ],
                [
                    'Escape',
                    () => {
                        e.preventDefault();
                        setDropdownOpen(false);
                        inputRef.current?.focus();
                    },
                ],
                [
                    'ArrowDown',
                    () => {
                        e.preventDefault();
                        options[idx + 1]?.focus();
                    },
                ],
                [
                    'ArrowUp',
                    () => {
                        e.preventDefault();
                        (idx === 0
                            ? inputRef.current
                            : options[idx - 1]
                        )?.focus();
                    },
                ],
            ]).get(e.key)?.();
        },
        [getOptions, selectColumn],
    );

    const handleValueOptionKeyDown = useCallback(
        (e: KeyboardEvent<HTMLButtonElement>, option: SearchOption) => {
            const options = getOptions();
            const idx = options.indexOf(e.currentTarget);
            new Map<string, () => void>([
                [
                    'Enter',
                    () => {
                        e.preventDefault();
                        commitOption(option);
                    },
                ],
                [
                    'Escape',
                    () => {
                        e.preventDefault();
                        cancelPendingColumn();
                    },
                ],
                [
                    'ArrowDown',
                    () => {
                        e.preventDefault();
                        options[idx + 1]?.focus();
                    },
                ],
                [
                    'ArrowUp',
                    () => {
                        e.preventDefault();
                        (idx === 0
                            ? inputRef.current
                            : options[idx - 1]
                        )?.focus();
                    },
                ],
            ]).get(e.key)?.();
        },
        [getOptions, commitOption, cancelPendingColumn],
    );

    const handleDateOptionKeyDown = useCallback(
        (e: KeyboardEvent<HTMLButtonElement>, option: SearchOption) => {
            const options = getOptions();
            const idx = options.indexOf(e.currentTarget);
            new Map<string, () => void>([
                [
                    'Enter',
                    () => {
                        e.preventDefault();
                        selectDateOption(option);
                    },
                ],
                [
                    'Escape',
                    () => {
                        e.preventDefault();
                        cancelPendingColumn();
                    },
                ],
                [
                    'ArrowDown',
                    () => {
                        e.preventDefault();
                        options[idx + 1]?.focus();
                    },
                ],
                [
                    'ArrowUp',
                    () => {
                        e.preventDefault();
                        (idx === 0
                            ? inputRef.current
                            : options[idx - 1]
                        )?.focus();
                    },
                ],
            ]).get(e.key)?.();
        },
        [getOptions, selectDateOption, cancelPendingColumn],
    );

    const handleNumberOptionKeyDown = useCallback(
        (e: KeyboardEvent<HTMLButtonElement>, option: SearchOption) => {
            const options = getOptions();
            const idx = options.indexOf(e.currentTarget);
            new Map<string, () => void>([
                [
                    'Enter',
                    () => {
                        e.preventDefault();
                        selectNumberOption(option);
                    },
                ],
                [
                    'Escape',
                    () => {
                        e.preventDefault();
                        cancelPendingColumn();
                    },
                ],
                [
                    'ArrowDown',
                    () => {
                        e.preventDefault();
                        options[idx + 1]?.focus();
                    },
                ],
                [
                    'ArrowUp',
                    () => {
                        e.preventDefault();
                        (idx === 0
                            ? inputRef.current
                            : options[idx - 1]
                        )?.focus();
                    },
                ],
            ]).get(e.key)?.();
        },
        [getOptions, selectNumberOption, cancelPendingColumn],
    );

    // Close dropdowns when clicking outside the search container
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent): void => {
            const path = e.composedPath();
            if (containerRef.current && !path.includes(containerRef.current)) {
                setDropdownOpen(false);
                setValueDropdownOpen(false);
                setDateOptionDropdownOpen(false);
                setDateDropdownOpen(false);
                setNumberOptionDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const showColumnDropdown =
        dropdownOpen && !pendingColumn && filteredColumns.length > 0;
    const showValueDropdown =
        valueDropdownOpen && pendingColumn?.inputType === 'select';
    const showDateOptionDropdown =
        dateOptionDropdownOpen && pendingColumn?.inputType === 'date';
    const showDateDropdown =
        dateDropdownOpen && pendingColumn?.inputType === 'date';
    const showNumberOptionDropdown =
        numberOptionDropdownOpen && pendingColumn?.inputType === 'number';

    const grouped = useMemo(
        () => groupByCategory(filteredColumns),
        [filteredColumns],
    );

    return {
        inputRef,
        containerRef,
        appliedFilters,
        pendingColumn,
        inputValue,
        showColumnDropdown,
        showValueDropdown,
        showDateOptionDropdown,
        showDateDropdown,
        pendingDateOption,
        selectDateOption,
        showNumberOptionDropdown,
        pendingNumberOption,
        selectNumberOption,
        handleNumberOptionKeyDown,
        grouped,
        filteredValueOptions,
        handleInputChange,
        handleInputFocus,
        handleKeyDown,
        handleColumnOptionKeyDown,
        handleValueOptionKeyDown,
        removeFilter,
        commitOption,
        selectColumn,
        handleDateOptionKeyDown,
    };
}
